import React, { useEffect, useRef, useState } from 'react';

/**
 * FlickerTest — FPS Module 4 (replaces Memory)
 * 30 trials. Each target is visible for 500ms then disappears.
 * Player must click inside the window — late clicks are ignored.
 * Records RT, hit/miss, and normalized distance from center.
 */
export default function FlickerTest({ trials, onComplete, icon, moduleNum = 4, totalModules = 6 }) {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    trialIndex: 0,
    onsetTime: null,
    windowOpen: false,
    results: [],
    timeoutId: null,
  });

  const runNextTrialRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const warmupPhase = useRef(true);
  const [warmupComplete, setWarmupComplete] = useState(false);

  const [uiState, setUiState] = useState('instructions');
  const [progress, setProgress] = useState(0);

  const WARMUP_FLICKERS = [
    { x: 0.5, y: 0.5, radius: 48, isi: 700, displayDuration: 800 },
    { x: 0.3, y: 0.38, radius: 44, isi: 700, displayDuration: 800 },
    { x: 0.7, y: 0.62, radius: 44, isi: 700, displayDuration: 800 },
  ];

  useEffect(() => {
    document.body.style.cursor = uiState === 'running' ? 'crosshair' : '';
    return () => { document.body.style.cursor = ''; };
  }, [uiState]);

  // --- Drawing ---

  const clearCanvas = (ctx, canvas) => {
    ctx.fillStyle = '#0a0e1a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const drawCrosshair = (ctx, canvas) => {
    clearCanvas(ctx, canvas);
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    ctx.strokeStyle = 'rgba(255,255,255,0.2)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(cx - 12, cy);
    ctx.lineTo(cx + 12, cy);
    ctx.moveTo(cx, cy - 12);
    ctx.lineTo(cx, cy + 12);
    ctx.stroke();
  };

  const drawTarget = (ctx, canvas, trial, flash = null) => {
    clearCanvas(ctx, canvas);
    const cx = trial.x * canvas.width;
    const cy = trial.y * canvas.height;
    const radius = trial.radius || 32;

    if (flash === 'hit') {
      ctx.fillStyle = 'rgba(16,185,129,0.25)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    } else if (flash === 'miss') {
      ctx.fillStyle = 'rgba(239,68,68,0.18)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fillStyle = flash === 'hit' ? '#10b981' : flash === 'miss' ? '#ef4444' : '#ff6b35';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Center dot
    ctx.beginPath();
    ctx.arc(cx, cy, 4, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.fill();
  };

  // --- Core logic ---

  runNextTrialRef.current = () => {
    const s = stateRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const activeTrials = warmupPhase.current ? WARMUP_FLICKERS : trials;

    if (s.trialIndex >= activeTrials.length) {
      if (warmupPhase.current) {
        setWarmupComplete(true);
        setTimeout(() => {
          warmupPhase.current = false;
          s.trialIndex = 0;
          s.results = [];
          s.windowOpen = false;
          s.onsetTime = null;
          setWarmupComplete(false);
          setProgress(0);
          setTimeout(() => runNextTrialRef.current?.(), 200);
        }, 2000);
        return;
      }
      setProgress(100);
      setUiState('done');
      onCompleteRef.current(s.results);
      return;
    }

    const trial = activeTrials[s.trialIndex];
    s.windowOpen = false;
    s.onsetTime = null;
    if (!warmupPhase.current) {
      setProgress(Math.round((s.trialIndex / trials.length) * 100));
    }

    // Show crosshair during ISI
    drawCrosshair(ctx, canvas);

    s.timeoutId = setTimeout(() => {
      const currentCanvas = canvasRef.current;
      if (!currentCanvas) return;
      const currentCtx = currentCanvas.getContext('2d');
      requestAnimationFrame(() => {
        drawTarget(currentCtx, currentCanvas, trial);
        s.onsetTime = performance.now();
        s.windowOpen = true;

        // Target disappears after displayDuration — window closes
        s.timeoutId = setTimeout(() => {
          if (s.windowOpen) {
            s.windowOpen = false;

            // Record miss only during real test
            if (!warmupPhase.current) {
              s.results.push({ type: 'flicker', rt: null, correct: false, distFromCenter: null });
            }
            s.trialIndex++;

            requestAnimationFrame(() => {
              drawTarget(currentCtx, currentCanvas, trial, 'miss');
              s.timeoutId = setTimeout(() => runNextTrialRef.current?.(), 400);
            });
          }
        }, trial.displayDuration || 500);
      });
    }, trial.isi || 600);
  };

  // --- Input handlers ---

  useEffect(() => {
    if (uiState !== 'running') return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const getCanvasCoords = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
      };
    };

    const handleInput = (clientX, clientY) => {
      const s = stateRef.current;
      if (!s.windowOpen || s.onsetTime === null) return;

      s.windowOpen = false;
      clearTimeout(s.timeoutId);

      const c = canvasRef.current;
      const ctx = c ? c.getContext('2d') : null;
      const activeTrials = warmupPhase.current ? WARMUP_FLICKERS : trials;
      const trial = activeTrials[s.trialIndex];

      const { x, y } = getCanvasCoords(clientX, clientY);
      const rt = Math.round(performance.now() - s.onsetTime);
      const cx = trial.x * c.width;
      const cy = trial.y * c.height;
      const dx = x - cx;
      const dy = y - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const radius = trial.radius || 32;
      const correct = dist <= radius;
      const distFromCenter = dist / radius;

      if (ctx) {
        requestAnimationFrame(() => drawTarget(ctx, c, trial, correct ? 'hit' : 'miss'));
      }

      // Only push to results during real test
      if (!warmupPhase.current) {
        s.results.push({ type: 'flicker', rt, correct, distFromCenter });
      }
      s.trialIndex++;

      s.timeoutId = setTimeout(() => runNextTrialRef.current?.(), 400);
    };

    const handleMouseDown = (e) => handleInput(e.clientX, e.clientY);
    const handleTouchStart = (e) => {
      e.preventDefault();
      const touch = e.changedTouches[0];
      handleInput(touch.clientX, touch.clientY);
    };

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('touchstart', handleTouchStart);
    };
  }, [uiState]); // eslint-disable-line

  // --- Cleanup ---

  useEffect(() => {
    return () => {
      clearTimeout(stateRef.current.timeoutId);
    };
  }, []);

  // --- Start ---

  const startTest = () => {
    stateRef.current.trialIndex = 0;
    stateRef.current.results = [];
    stateRef.current.windowOpen = false;
    stateRef.current.onsetTime = null;
    setProgress(0);
    setUiState('running');
    setTimeout(() => runNextTrialRef.current?.(), 500);
  };

  const startWarmup = () => {
    warmupPhase.current = true;
    startTest();
  };

  // --- Render ---

  return (
    <div style={{ width: '100%' }}>
      {uiState === 'instructions' && (
        <div className="card text-center" style={{ maxWidth: 480, margin: '0 auto' }}>
          {icon && <div style={{ width: 56, height: 56, borderRadius: 14, background: 'var(--accent-dim)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', margin: '0 auto 20px' }}>{icon}</div>}
          <h2 style={{ marginBottom: 8 }}>Flick Speed</h2>
          <p style={{ marginBottom: 8 }}>
            A target will flash on screen. Click it before it disappears — you have only{' '}
            <strong style={{ color: 'var(--accent)' }}>500ms</strong>.
          </p>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 4 }}>
            Input: <span className="text-accent mono">CLICK / TAP</span>
          </p>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 24 }}>
            30 trials — clicks after the target disappears do not count.
          </p>
          <button className="btn btn--primary" onClick={startWarmup}>
            Start
          </button>
        </div>
      )}

      {uiState === 'running' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="progress-bar" style={{ flex: 1 }}>
              <div className="progress-bar__fill" style={{ width: `${progress}%` }} />
            </div>
            <span style={{ fontSize: 13, whiteSpace: 'nowrap', color: warmupPhase.current && !warmupComplete ? '#f59e0b' : 'var(--text-muted)' }}>
              {warmupPhase.current && !warmupComplete ? 'PRACTICE — not scored' : `${progress}%`}
            </span>
          </div>
          <div style={{ position: 'relative' }}>
            <div className="canvas-wrapper">
              <canvas
                ref={canvasRef}
                width={800}
                height={520}
                style={{ width: '100%', maxWidth: 800, background: '#0a0e1a', cursor: 'crosshair', display: 'block' }}
              />
            </div>
            {warmupComplete && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(10,14,26,0.92)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                borderRadius: 8,
              }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#10b981', marginBottom: 8 }}>
                  Warmup Complete!
                </div>
                <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }}>
                  Starting real test in 2 seconds...
                </div>
              </div>
            )}
          </div>
          <p className="text-center text-muted" style={{ fontSize: 13 }}>
            Module {moduleNum} of {totalModules} — Flick Speed
          </p>
        </div>
      )}
    </div>
  );
}
